import { httpService, type HttpError } from '../services/httpService';
import { eventBus } from '../eventBus';

import type { ClientesPreencher_GenerateLinkResponseDados } from '../contracts/ClientesPreencher_GenerateLinkResponse';
import type { ClientesPreencher_GetPublicTokenResponseDados } from '../contracts/ClientesPreencher_GetPublicTokenResponse';
import type { ClientesPreencher_PublicUpdateResponseDados } from '../contracts/ClientesPreencher_PublicUpdateResponse';
import type { Orders_PickupNotificationCreateResponse } from '../contracts/Orders_PickupNotificationCreateResponse';

type GenerateLinkResponse = ClientesPreencher_GenerateLinkResponseDados;
type TokenValidationResponse = ClientesPreencher_GetPublicTokenResponseDados;
type UpdateResponse = ClientesPreencher_PublicUpdateResponseDados;

export type PreCadastroLinkOutcome = 'opened' | 'complete' | 'error';

export interface PublicFormData {
  email: string;
  cpf: string;
  data_nascimento: string;
  genero: string;
  cep: string;
  rua: string;
  numero: string;
  complemento: string;
  bairro: string;
}

export interface FormErrors {
  email: string;
  cpf: string;
  data_nascimento: string;
  cep: string;
  rua: string;
  bairro: string;
}

// ── API calls ──────────────────────────────────────────────────────

export function useClientePreCadastro() {
  /**
   * Generate a pre-registration fulfillment link for a customer.
   */
  async function generateLink(clienteId: number): Promise<GenerateLinkResponse | null> {
    const res = await httpService.post<{ dados: GenerateLinkResponse }>(
      '/index.php/clientes_preencher/generate_link',
      { cliente_id: clienteId },
      {
        inputContract: 'ClientesPreencher_GenerateLinkInput',
        responseContract: 'ClientesPreencher_GenerateLinkResponse',
        throwOnEnvelopeFailure: 'Não foi possível gerar o link de pré-cadastro.',
      },
    );
    return res?.dados ?? null;
  }

  /**
   * Validate a public token and retrieve customer + document config.
   */
  async function validateToken(token: string): Promise<TokenValidationResponse | null> {
    const res = await httpService.get<{ dados: TokenValidationResponse }>(
      `/index.php/clientes/preencher/${encodeURIComponent(token)}`,
      { responseContract: 'ClientesPreencher_GetPublicTokenResponse' },
    );
    return res?.dados ?? null;
  }

  /**
   * Submit the public pre-registration form.
   */
  async function submitUpdate(token: string, payload: Record<string, unknown>): Promise<UpdateResponse | null> {
    const res = await httpService.post<{ dados: UpdateResponse }>(
      '/index.php/clientes/preencher/update',
      { token, ...payload },
      { inputContract: 'ClientesPreencher_PublicUpdateInput', responseContract: 'ClientesPreencher_PublicUpdateResponse' },
    );
    return res?.dados ?? null;
  }

  /**
   * Post an audit notification for WhatsApp delivery (best-effort).
   */
  async function postPickupNotification(note: string, orderItemId?: number): Promise<void> {
    try {
      await httpService.post<Orders_PickupNotificationCreateResponse>(
        '/index.php/so/entregas/pickup_notifications',
        {
          order_item_id: orderItemId ?? 0,
          channel: 'whatsapp',
          notes: note.slice(0, 500),
        },
        { inputContract: 'Orders_PickupNotificationCreateInput', responseContract: 'Orders_PickupNotificationCreateResponse' },
      );
    } catch (_e) {
      // Best-effort — no UI feedback needed
    }
  }

  /**
   * Gera o link de pré-cadastro e, se o perfil estiver incompleto, abre o modal
   * de envio por WhatsApp. Retorna 'opened' se abriu (perfil incompleto),
   * 'complete' se não havia o que enviar (perfil já completo / sem link) e
   * 'error' se o link não pôde ser gerado — o motivo já foi mostrado aqui, então
   * o chamador não repete a mensagem. Reusável: o form de cliente chama após
   * salvar, e o ClienteModal expõe um botão para reabrir.
   */
  async function offerPreCadastroLink(clienteId: number, fallbackNome = ''): Promise<PreCadastroLinkOutcome> {
    if (!clienteId) return 'complete';
    try {
      const dados = await generateLink(Number(clienteId));
      if (!dados?.is_new_client || !dados?.link_url) return 'complete';
      eventBus.emit('modal.pdv-enviar-whatsapp.open', {
        tipo: 'pre_cadastro',
        clienteId: Number(clienteId),
        clienteNome: dados.cliente_nome || fallbackNome,
        clienteTelefone: dados.cliente_telefone || '',
        isNewClient: true,
        linkPreCadastro: dados.link_url,
      });
      return 'opened';
    } catch (e) {
      // Uma falha de transporte (HTTP não-2xx) já virou toast no httpService;
      // a recusa do backend (HTTP 200, success:false) é nossa de mostrar.
      if ((e as HttpError)?.kind === 'envelope-failure') {
        eventBus.emit('globalMessage.error', (e as Error).message);
      }
      return 'error';
    }
  }

  return { generateLink, validateToken, submitUpdate, postPickupNotification, offerPreCadastroLink };
}

// ── Pure utilities (no HTTP) ───────────────────────────────────────

/**
 * Validate the public pre-registration form.
 */
export function validatePreCadastroForm(
  form: PublicFormData,
  errors: FormErrors,
  docType: string,
  t: (key: string) => string,
): boolean {
  let valid = true;
  Object.keys(errors).forEach((k) => (errors[k as keyof FormErrors] = ''));

  const email = form.email.trim();
  if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    errors.email = t('Informe um e-mail válido.');
    valid = false;
  }

  const doc = form.cpf.replace(/\D/g, '');
  const docLen = docType === 'rut' ? 9 : 11;
  if (doc && doc.length !== docLen) {
    // Built from translated FRAGMENTS rather than t('{docType} deve ter
    // {docLen} dígitos.'). A key carrying placeholders renders LITERALLY until
    // it exists in the catalog — vue-i18n's `missing` return value is used
    // verbatim, never interpolated — so this line used to ship "{docType} deve
    // ter {docLen} dígitos." to the user and carried a manual
    // `includes('{docType}')` patch to paper over it. Fragments have no
    // placeholder to leak.
    errors.cpf = `${docType.toUpperCase()} ${t('deve ter')} ${docLen} ${t('dígitos')}.`;
    valid = false;
  }

  return valid;
}

/**
 * Apply document mask (CPF or RUT) based on country.
 */
export function maskDocument(value: string, docType: string): string {
  if (docType === 'rut') {
    let v = value.replace(/[^0-9kK]/g, '');
    if (v.length > 9) v = v.slice(0, 9);
    if (v.length > 8) {
      return v.replace(/^(\d{1,2})(\d{3})(\d{3})([\dkK])$/, '$1.$2.$3-$4');
    } else if (v.length > 5) {
      return v.replace(/^(\d{1,2})(\d{3})(\d{3})/, '$1.$2.$3');
    } else if (v.length > 2) {
      return v.replace(/^(\d{1,2})(\d{3})/, '$1.$2');
    }
    return v;
  }

  // Default: CPF
  let v = value.replace(/\D/g, '');
  if (v.length > 11) v = v.slice(0, 11);
  if (v.length > 9) {
    return v.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
  } else if (v.length > 6) {
    return v.replace(/^(\d{3})(\d{3})(\d{3})/, '$1.$2.$3');
  } else if (v.length > 3) {
    return v.replace(/^(\d{3})(\d{3})/, '$1.$2');
  }
  return v;
}

/**
 * Look up a Brazilian CEP via ViaCEP (guarded by country).
 * Does nothing for non-Brazil countries.
 */
export async function buscarCep(
  cep: string,
  country: string,
  onResult: (data: { logradouro?: string; bairro?: string }) => void,
): Promise<void> {
  if (country !== 'BR') return;

  const cleanCep = cep.replace(/\D/g, '');
  if (cleanCep.length !== 8) return;

  try {
    const { httpService } = await import("../services/httpService");
    const data = await httpService.get<any>(`https://viacep.com.br/ws/${cleanCep}/json/`);
    if (!data.erro) {
      onResult({ logradouro: data.logradouro || '', bairro: data.bairro || '' });
    }
  } catch (_e) {
    // Best-effort — CEP lookup fails silently
  }
}
